import {
  useGetJewelryVariation,
  useMatchingBandData,
} from "@apis/diamondDetail";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import {
  Diamond,
  DisplayConfigurationV2,
  SlugDetail,
} from "@src/types/DiamondDetail";
import { Variations } from "@src/types/JewelryVariationDetails";
import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  buildNestedParams,
  updateURL,
  isObjectWithSelectedSlugs,
  isRectangularTextButtonWithPrice,
  sortAllTypes,
  typeUpdate,
} from "../utils";
import { JCSelectedOption, Personalization } from "../types";
import { useTranslation } from "react-i18next";
import {
  isJewelry,
  isThreeDEnabled,
  addThreeDAdditionalSettingsTypes,
} from "@utils/product/productValidators";
import { DeviceEventEmitter, Platform } from "react-native";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { isEqual } from "lodash";
import { ProductCategory } from "@utils/constants";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useNavigationHistory } from "../../../../hooks/useNavigationHistory";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { usePdpSectionLayouts } from "@scenes/pdp/hooks/usePdpSectionLayouts";

interface JewelryConfiguratorContextType {
  defaultSlug: SlugDetail;
  setDefaultSlug: (value: SlugDetail) => void;
  userSelectedSlug: SlugDetail;
  setUserSelectedSlug: (value: SlugDetail) => void;
  isVariationRequired: boolean;
  setIsVariationRequired: React.Dispatch<React.SetStateAction<boolean>>;
  allTypes: DisplayConfigurationV2[];
  setAllTypes: (value: Diamond) => void;
  updateJewelryConfigInfo: (
    optionType: string,
    values: [DisplayConfigurationV2, string]
  ) => void;
  customDesignConfiguration: boolean;
  setCustomDesignConfiguration: React.Dispatch<React.SetStateAction<boolean>>;
  itemDetail: Diamond;
  setItemDetail: (value: Diamond) => void;
  jcSelectedOption: React.MutableRefObject<JCSelectedOption>;
  variationListRef: React.MutableRefObject<Variations[]>;
  isAPILoading: boolean;
  resetDefaultSlug: (isJewelryBuilder?: boolean) => void;
  personalization: Personalization;
  setPersonalization: (value?: Personalization | null) => void;
  hasPersonalizationError: boolean;
  setHasPersonalizationError: (value: boolean) => void;
  updateJcSelectedOptions: (
    variation: Diamond,
    enterSecondFlow?: boolean
  ) => void;
  validateRequiredOptions: () => void;
  addToCartPressed: boolean;
  setAddToCartPressed: React.Dispatch<React.SetStateAction<boolean>>;
  fetchValidationMessage: (optionName: string, isInput: boolean) => string;
  appendNewVariation: (variations: Variations[]) => void;
  stoneItemDetail: Diamond;
  setStoneItemDetail: (value: Diamond) => void;
  matchingPairItemDetail: Diamond;
  setMatchingPairItemDetail: (value: Diamond) => void;
  reviewTotalPrice: Number;
  setReviewTotalPrice: (value: Number) => void;
  addToOption: (
    optionType: string,
    values: [DisplayConfigurationV2, any]
  ) => void;
  resetVariationList: () => void;
  clearItemDetail: (isJewelryProduct: boolean) => void;
  getDynamicParams: (
    full_option_slugs: Record<string, string>,
    partial_option_slugs: Record<string, string>,
    fetchVariation: boolean,
    payload: any
  ) => Record<string, string>;
  applyPersonalizationEngravedText: () => void;
  addThreeDAdditionalSettings: (
    three_d_media: any[],
    type: addThreeDAdditionalSettingsTypes
  ) => void;
  matchingBandData: Diamond[];
  resetDynamicParams: () => void;
}

const JewelryConfiguratorContext = createContext<
  JewelryConfiguratorContextType | undefined
>(undefined);

export const JewelryConfiguratorProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [defaultSlug, setDefaultSlugState] = useState<SlugDetail>({});
  const [userSelectedSlug, setUserSelectedSlugState] = useState<SlugDetail>({});
  const { getUserPriceMode, getFeatureSettings, getUserMarkupAmount } =
    useUserInfo();
  const { t } = useTranslation();
  const { initStatus } = useOrgSettingsSlice();

  const [isVariationRequired, setIsVariationRequired] = useState(false);

  const [itemDetail, setItemDetailState] = useState<Diamond>();
  const [stoneItemDetail, setStoneItemDetailState] = useState<Diamond>();
  const [matchingPairItemDetail, setMatchingPairItemDetailState] =
    useState<Diamond>();
  const [reviewTotalPrice, setReviewTotalPriceState] = useState<Number>();
  const [matchingBandData, setMatchingBandDataState] = useState<Diamond[]>([]);
  const jcSelectedOption = useRef<JCSelectedOption>({
    option_slug: {},
    option_value: {},
    option_required: {},
    option_searchable: {},
    option_mode: {},
    option_visible: [],
    option_error: {},
  });
  const [allTypes, setAllTypesState] = useState<DisplayConfigurationV2[]>([]);
  const [variation_slug, setVariationSlug] = useState("");
  const [personalization, setPersonalizationState] =
    useState<Personalization | null>(null);
  const [hasPersonalizationError, setHasPersonalizationError] = useState(false);
  const variationListRef = useRef<Variations[]>([]);
  const [customDesignConfiguration, setCustomDesignConfiguration] =
    useState(false);
  const [defaultMorphValuesForThinkspace, setDefaultMorphValuesForThinkspace] =
    useState({
      length: null,
      thickness: null,
    });

  const [defaultheadScaleValue, setDefaultheadScaleValue] = useState({
    scale: null,
  });

  const setDefaultSlug = (value: SlugDetail) => {
    setDefaultSlugState(value);
  };

  const setPersonalization = (value?: Personalization) => {
    setPersonalizationState(value);
    setShouldSync((prev) => !prev);
  };

  const setUserSelectedSlug = (value: SlugDetail) => {
    setUserSelectedSlugState(value);
    jcSelectedOption.current.option_slug = safeParse(value);
  };

  const [addToCartPressed, setAddToCartPressed] = useState(false);
  const [shouldSync, setShouldSync] = useState(false);

  const [dynamicParams, setDynamicParams] = useState<Record<string, any>>({});
  const fetchDisabledVariationsForPrices = useRef(false);

  const pageSize = getFeatureSettings()?.variations_price_attachment ? 10 : 1;

  const engraveDebounceRef = useRef(null);
  const isGuest = useSelector((state: RootState) => state.userInfo.isGuest);
  const { personalizationSectionLayout } = usePdpSectionLayouts({
    product_type: itemDetail?.type as ProductCategory,
  });
  const initialHeadModelUrlRef = useRef<string | null>(null);
  const productId =
    itemDetail?.type !== ProductCategory.JEWELRY &&
    itemDetail?.type !== ProductCategory.LABGROWN_JEWELRY
      ? stoneItemDetail?.id
      : itemDetail?.id;

  const jewelryItem =
    itemDetail?.type !== ProductCategory.JEWELRY &&
    itemDetail?.type !== ProductCategory.LABGROWN_JEWELRY
      ? stoneItemDetail
      : itemDetail;

  const {
    data: VariationData,
    isSuccess,
    isFetching: isAPILoading,
  } = useGetJewelryVariation(
    productId as number,
    getUserPriceMode(),
    pageSize,
    isVariationRequired || jewelryItem?.variation_required,
    dynamicParams,
    undefined,
    undefined,
    getUserMarkupAmount()
  );
  const { replaceProductIdInHistory } = useNavigationHistory();

  const { mutateAsync: refetchMatchingBandData } = useMatchingBandData();

  const latestMatchingBandProductIdRef = useRef<number | string | null>(null);

  const fetchMatchingBandDataForProduct = useCallback(
    async (mProductId: number | string, matchGroupId: string) => {
      latestMatchingBandProductIdRef.current = mProductId;
      try {
        const result = await refetchMatchingBandData({
          productId: mProductId,
          matchGroupId,
        });
        // Discard if a newer fetch for a different product has already started.
        if (latestMatchingBandProductIdRef.current !== mProductId) {
          return;
        }
        const data = result?.data?.items;
        if (data?.length > 0) {
          setMatchingBandDataState(data);
        } else {
          setMatchingBandDataState([]);
        }
      } catch {
        if (latestMatchingBandProductIdRef.current === mProductId) {
          setMatchingBandDataState([]);
        }
      }
    },
    [refetchMatchingBandData]
  );

  const setItemDetail = (value: Diamond) => {
    if (!isEqual(itemDetail, value)) {
      setItemDetailState(value);
      setShouldSync((prev) => !prev);
      if (isJewelry(value?.type)) {
        jcSelectedOption.current.option_slug = safeParse(
          value?.slug_detail ?? {}
        );
        if (value?.id && value?.match_group_id) {
          fetchMatchingBandDataForProduct(value.id, value.match_group_id);
        } else {
          setMatchingBandDataState([]);
        }
        if (isJewelry(value?.type) && value?.display_configuration_v2) {
          setAllTypes(value);
          if (Platform.OS === "web" && value?.id) {
            updateURL(value.id);
          }
          replaceProductIdInHistory(value.id as number);
        }
      }
    }
  };

  const clearItemDetail = (isJewelryProduct: boolean) => {
    setItemDetailState(undefined);
    if (isJewelryProduct) {
      jcSelectedOption.current.option_slug = {};
      jcSelectedOption.current.option_value = {};
      jcSelectedOption.current.option_required = {};
      jcSelectedOption.current.option_searchable = {};
      jcSelectedOption.current.option_mode = {};
      jcSelectedOption.current.option_visible = [];
      jcSelectedOption.current.option_error = {};
      if (Platform.OS === "web") {
        localStorage.removeItem("jcSelectedOption");
      }
    }
  };

  const setReviewTotalPrice = (value: Number) => {
    if (value) {
      setReviewTotalPriceState(value);
    }
  };

  const normalizeSlug = (slug: string) => {
    return slug.toLowerCase().replaceAll(" ", "-");
  };

  const setStoneItemDetail = (value: Diamond) => {
    if ((stoneItemDetail?.id ?? "") !== value.id) {
      setStoneItemDetailState(value);
      setShouldSync((prev) => !prev);
      if (isJewelry(value?.type)) {
        jcSelectedOption.current.option_slug = safeParse(
          value?.slug_detail ?? {}
        );
        if (isJewelry(value?.type) && value?.display_configuration_v2) {
          setAllTypes(value);
          if (Platform.OS === "web" && value?.id) {
            updateURL(value.id);
          }
          replaceProductIdInHistory(value.id as number);
        }
      }
    }
  };

  const setMatchingPairItemDetail = (value: Diamond) => {
    if (matchingPairItemDetail?.id !== value.id) {
      setMatchingPairItemDetailState(value);
      setShouldSync((prev) => !prev);
    }
  };

  const setAllTypes = (value: Diamond) => {
    setAllTypesState(priceUpdate(sortAllTypes(typeUpdate(value))));
  };

  const priceUpdate = (allItems: DisplayConfigurationV2[]) => {
    const allData = allItems.map((type) => {
      if (isRectangularTextButtonWithPrice(type.option_style)) {
        type.option_value = type.option_value.map((value) => {
          const price = calculatePrice(type, value.slug);
          return {
            ...value,
            price,
          };
        });
      }
      return type;
    });
    return allData;
  };

  const calculatePrice = (type, values) => {
    const full_option_slugs = Object.assign(
      {},
      jcSelectedOption.current.option_slug
    );
    full_option_slugs[type.option_type] = values?.toLowerCase();
    const variationSlug = Object.entries(full_option_slugs)
      .map(([_, value]) => value.replaceAll(" ", "-"))
      .join("-");
    const variant_slug_data = variationListRef.current?.find(
      (item) => item["slug-name"] === variationSlug?.toLowerCase()
    )?.data;
    // const variant_item = variationListRef?.current.find(
    //   (item) => item["slug-name"] === variationSlug
    // )?.item;
    const price = variant_slug_data?.price || "-";
    return price;
  };

  const updateJewelryConfigInfo = async (
    optionType: string,
    values: [DisplayConfigurationV2, string],
    fetchVariation: boolean = true
  ) => {
    if (optionType in jcSelectedOption.current.option_slug) {
      const isOptionEnabled = values[0].option_value.find(
        (value) => value.slug === values[1] || value.value === values[1]
      )?.enabled;
      fetchDisabledVariationsForPrices.current = !isOptionEnabled;
      const partial_option_slugs = isOptionEnabled
        ? getOptionSlugsFromTop(optionType)
        : { [optionType]: values[1] };
      const full_option_slugs = isOptionEnabled
        ? Object.assign({}, jcSelectedOption.current.option_slug)
        : getOptionSlugsFromTop(optionType);
      partial_option_slugs[optionType] =
        typeof values[0] === "object"
          ? values[1].toLowerCase()
          : values[0]?.toLowerCase();
      full_option_slugs[optionType] =
        typeof values[0] === "object"
          ? values[1].toLowerCase()
          : values[0].toLowerCase();
      setVariationSlug(
        Object.entries(full_option_slugs)
          .map(([_, value]) => String(value).toLowerCase().replaceAll(" ", "-"))
          .join("-")
      );
      if (fetchVariation) {
        const variant = variationListRef.current.filter(
          (item) =>
            item["slug-name"] ===
            Object.entries(full_option_slugs)
              .map(([_, value]) => value?.toLowerCase()?.replaceAll(" ", "-"))
              .join("-")
        )[0];
        if (!getFeatureSettings()?.variations_price_attachment && variant) {
          setUserSelectedSlug(variant?.item?.slug_detail ?? {});
          if (itemDetail?.type === ProductCategory.JEWELRY) {
            setItemDetail(variant?.item);
            noPriceForConfiguration(variant?.item);
          } else {
            setStoneItemDetail(variant?.item);
          }
          if (isThreeDEnabled(variant.item) && variant?.item?.three_d_media) {
            const threeDMedia = addThreeDAdditionalSettings(
              addThreeDAdditionalSettings(
                variant?.item?.three_d_media,
                addThreeDAdditionalSettingsTypes.morph
              ),
              addThreeDAdditionalSettingsTypes.headScale
            );
            DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
              iframeKey: [
                "vdb-gallery-iframe",
                "vdb-sticky-iframe",
                "vdb-overlay-iframe",
              ],
              data: {
                three_d_data_type:
                  initStatus?.three_d_settings?.three_d_data_type || "webgi",
                three_d_media: threeDMedia,
                vjson: initStatus?.three_d_settings?.vjson,
              },
            });
          }
          updateJcSelectedOptions(variant?.item);
        } else {
          const filteredPartialSlugs = Object.entries(partial_option_slugs)
            .filter(([_, value]) => value !== "")
            .reduce((acc, [key, value]) => {
              acc[key] = value as string;
              return acc;
            }, {} as Record<string, string>);

          const filteredFullSlugs = Object.entries(full_option_slugs)
            .filter(([_, value]) => value !== "")
            .reduce((acc, [key, value]) => {
              acc[key] = value as string;
              return acc;
            }, {} as Record<string, string>);
          let partial_slug_detail = {};
          let slug_detail = {};
          Object.keys(filteredPartialSlugs).forEach((slug) => {
            const slugValue = filteredPartialSlugs[slug];
            const allItemType = allTypes.find(
              (item) => item.option_type === slug
            );
            if (typeof slugValue === "string" && allItemType?.option_value) {
              const matchedValue = allItemType.option_value.find(
                (item) =>
                  item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
              )?.value;
              if (matchedValue) {
                partial_slug_detail[slug] = matchedValue;
              }
            }
          });
          Object.keys(filteredFullSlugs).forEach((slug) => {
            let allItemType = allTypes.find(function (item) {
              if (item.option_type === slug) {
                if (isRectangularTextButtonWithPrice(item.option_style)) {
                  if (
                    item.option_type === optionType &&
                    !values[0].option_value.find((x) => x.slug === values[1])
                      .enabled
                  ) {
                    return false;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              } else {
                return false;
              }
            });
            if (allItemType?.option_value) {
              let slugValue = filteredFullSlugs[slug];
              if (isObjectWithSelectedSlugs(slugValue)) {
                slug_detail[slug] = slugValue;
              } else if (typeof slugValue === "string") {
                if (
                  allItemType.option_value.find(
                    (item) =>
                      item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
                  )?.value
                ) {
                  slug_detail[slug] = allItemType.option_value.find(
                    (item) =>
                      item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
                  )?.value;
                }
              } else if (Array.isArray(slugValue)) {
                slug_detail[slug] = slugValue;
              }
            }
          });
          if (values?.[0]?.reinitialize_index_on_change) {
            DeviceEventEmitter.emit("RESET_GALLERY");
          }
          await setDynamicParams({
            ...buildNestedParams("partial_slug_details", partial_slug_detail),
            ...buildNestedParams("slug_details", slug_detail),
            _timestamp: Date.now().toString(),
          });
        }
      }
    } else {
      applyVariation(optionType, null, values);
    }
  };

  const getDynamicParams = (
    full_option_slugs: Record<string, string>,
    partial_option_slugs: Record<string, string>,
    fetchVariation: boolean,
    payload: any
  ) => {
    const firstKey = Object.keys(payload ?? {})[0];
    const payloadShapes = payload?.[firstKey]?.shapes ?? [];
    const payloadShape =
      Array.isArray(payloadShapes) && payloadShapes.length > 0
        ? payloadShapes[0]?.toLowerCase()
        : null;

    const full_option_slugs_payload =
      full_option_slugs.supported_shapes?.toLowerCase() !== payloadShape &&
      payloadShape
        ? { ...full_option_slugs, supported_shapes: payloadShape }
        : full_option_slugs;
    const partial_option_slugs_payload =
      partial_option_slugs.supported_shapes?.toLowerCase() !== payloadShape &&
      payloadShape
        ? { ...partial_option_slugs, supported_shapes: payloadShape }
        : partial_option_slugs;

    if (payloadShape) {
      const supportedShapesType = allTypes.find(
        (item) => item.option_type === "supported_shapes"
      );
      const resolvedShapeValue = supportedShapesType?.option_value?.find(
        (item) =>
          item.slug === payloadShape.replaceAll(" ", "-") ||
          item.value?.toLowerCase() === payloadShape
      )?.value;
      if (
        resolvedShapeValue &&
        jcSelectedOption.current.option_value.supported_shapes !==
          resolvedShapeValue
      ) {
        jcSelectedOption.current.option_value.supported_shapes =
          resolvedShapeValue;
      }
    }
    if (fetchVariation) {
      const filteredPartialSlugs = Object.entries(partial_option_slugs_payload)
        .filter(([_, value]) => value !== "")
        .reduce((acc, [key, value]) => {
          acc[key] = value as string;
          return acc;
        }, {} as Record<string, string>);

      // Filter out keys with empty values in full_option_slug
      const filteredFullSlugs = Object.entries(full_option_slugs_payload)
        .filter(([_, value]) => value !== "")
        .reduce((acc, [key, value]) => {
          acc[key] = value as string;
          return acc;
        }, {} as Record<string, string>);
      let partial_slug_detail = {};
      let slug_detail = {};
      Object.keys(filteredPartialSlugs).forEach((slug) => {
        const slugValue = filteredPartialSlugs[slug];
        const allItemType = allTypes.find((item) => item.option_type === slug);
        if (typeof slugValue === "string" && allItemType?.option_value) {
          const matchedValue = allItemType.option_value.find(
            (item) => item.slug === slugValue.toLowerCase()
          )?.value;
          if (matchedValue) {
            partial_slug_detail[slug] = matchedValue;
          }
        }
      });
      Object.keys(filteredFullSlugs).forEach((slug) => {
        let allItemType = allTypes.find(function (item) {
          if (item.option_type === slug) {
            if (isRectangularTextButtonWithPrice(item.option_style)) {
              return false;
            } else {
              return true;
            }
          } else {
            return false;
          }
        });
        if (allItemType?.option_value) {
          let slugValue = filteredFullSlugs[slug];
          if (isObjectWithSelectedSlugs(slugValue)) {
            slug_detail[slug] = slugValue;
          } else if (typeof slugValue === "string") {
            if (
              allItemType.option_value.find(
                (item) =>
                  item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
              )?.value
            ) {
              slug_detail[slug] = allItemType.option_value.find(
                (item) =>
                  item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
              )?.value;
            }
          } else if (Array.isArray(slugValue)) {
            slug_detail[slug] = slugValue;
          }
        }
      });
      return {
        ...buildNestedParams("partial_slug_details", partial_slug_detail),
        ...buildNestedParams("slug_details", slug_detail),
        _timestamp: Date.now().toString(),
      };
    }
  };

  useEffect(() => {
    const current = jcSelectedOption.current;
    const stoneDetails = stoneItemDetail;
    const mItemDetail = itemDetail;
    const matchingPairDetails = matchingPairItemDetail;
    if (
      current &&
      Object.keys(current.option_value || {}).length > 0 &&
      Platform.OS === "web"
    ) {
      localStorage.setItem("jcSelectedOption", JSON.stringify(current));
      localStorage.setItem("personalization", JSON.stringify(personalization));
    }
    if (stoneDetails && Platform.OS === "web") {
      localStorage.setItem("stoneItemDetails", JSON.stringify(stoneDetails));
    }
    if (mItemDetail && Platform.OS === "web") {
      localStorage.setItem("itemDetail", JSON.stringify(mItemDetail));
    }
    if (matchingPairDetails && Platform.OS === "web") {
      localStorage.setItem(
        "matchingPairItemDetails",
        JSON.stringify(matchingPairDetails)
      );
    }
  }, [shouldSync]);

  useEffect(() => {
    if (Platform.OS === "web") {
      const navEntry = performance.getEntriesByType(
        "navigation"
      )[0] as PerformanceNavigationTiming;

      if (navEntry?.type === "reload") {
        const currentUrl = window.location.href;
        const urlParams = new URLSearchParams(window.location.search);
        const productType = urlParams.get("productType");
        const isBuilderReviewPage = currentUrl.includes("jewelry-builder");
        const isJewelryPDP =
          productType === ProductCategory.JEWELRY ||
          productType === ProductCategory.LABGROWN_JEWELRY ||
          currentUrl.includes("/jewelry/") ||
          isBuilderReviewPage;

        if (!isJewelryPDP) {
          localStorage.removeItem("jcSelectedOption");
          localStorage.removeItem("personalization");
          localStorage.removeItem("stoneItemDetails");
          localStorage.removeItem("itemDetail");
          localStorage.removeItem("matchingPairItemDetails");
          return;
        }
      }

      const stored = localStorage.getItem("jcSelectedOption");
      if (stored) {
        const parsed = JSON.parse(stored);
        jcSelectedOption.current = parsed;
      }
      const personalizationData = localStorage.getItem("personalization");
      if (personalizationData && personalizationData !== "null") {
        const parsed = JSON.parse(personalizationData);
        setPersonalization(parsed);
      }
      const stoneDetails = localStorage.getItem("stoneItemDetails");
      const mItemDetail = localStorage.getItem("itemDetail");
      const matchingPairDetails = localStorage.getItem(
        "matchingPairItemDetails"
      );

      if (stoneDetails) {
        const parsedStoneDetails = JSON.parse(stoneDetails);
        setStoneItemDetail(parsedStoneDetails);
      }

      if (mItemDetail) {
        const parsedMItemDetail = JSON.parse(mItemDetail);
        setItemDetail(parsedMItemDetail);
      }

      if (matchingPairDetails) {
        setMatchingPairItemDetail(JSON.parse(matchingPairDetails));
      }
    }
  }, []);

  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      "CLEAR_JEWELRY_CONTEXT",
      () => {
        // Clear all jewelry configurator state
        setItemDetailState(undefined);
        setStoneItemDetailState(undefined);
        setMatchingPairItemDetailState(undefined);
        setPersonalizationState(null);
        setAllTypesState([]);
        setMatchingBandDataState([]);
        variationListRef.current = [];
        jcSelectedOption.current = {
          option_slug: {},
          option_value: {},
          option_required: {},
          option_searchable: {},
          option_mode: {},
          option_visible: [],
          option_error: {},
        };
        if (Platform.OS === "web") {
          localStorage.removeItem("jcSelectedOption");
          localStorage.removeItem("personalization");
          localStorage.removeItem("stoneItemDetails");
          localStorage.removeItem("itemDetail");
          localStorage.removeItem("matchingPairItemDetails");
        }
      }
    );

    return () => {
      subscription.remove();
    };
  }, []);

  const addToOption = (
    optionType: string,
    values: [DisplayConfigurationV2, any, boolean]
  ) => {
    if (itemDetail?.custom_design_configuration) {
      const partial_option_slugs = Object.assign(
        {},
        jcSelectedOption.current.option_slug
      );
      let full_option_slugs = Object.assign(
        {},
        jcSelectedOption.current.option_slug
      );
      const selectedValue = values[1];
      if (typeof selectedValue === "object" && selectedValue.selected_slugs) {
        if (typeof full_option_slugs[optionType] === "string") {
          try {
            full_option_slugs[optionType] = JSON.parse(
              full_option_slugs[optionType]
            );
          } catch (e) {
            console.error(e, "ERROR");
          }
        }
        if (full_option_slugs[optionType]?.selected_slugs) {
          if (values[0].option_style === "multiselect_checkbox_with_dropdown") {
            selectedValue.selected_slugs.forEach((slug) => {
              const normalizedSlug = normalizeSlug(slug);
              const normalizedSelectedSlugs = full_option_slugs[
                optionType
              ].selected_slugs.map((s) => normalizeSlug(s));

              const index = normalizedSelectedSlugs.indexOf(normalizedSlug);

              if (index === -1) {
                full_option_slugs[optionType].selected_slugs.push(slug); // Add the original slug
              } else {
                full_option_slugs[optionType].selected_slugs.splice(index, 1);
              }
            });
          } else {
            if (selectedValue.selected_slugs.length > 0) {
              selectedValue.selected_slugs.forEach((slug) => {
                const normalizedSlug = normalizeSlug(slug);
                const normalizedSelectedSlugs = full_option_slugs[
                  optionType
                ].selected_slugs.map((s) => normalizeSlug(s));

                const index = normalizedSelectedSlugs.indexOf(normalizedSlug);

                if (index === -1) {
                  if (full_option_slugs[optionType].selected_slugs.length > 0) {
                    full_option_slugs[optionType].selected_slugs[0] = slug;
                  } else {
                    full_option_slugs[optionType].selected_slugs.push(slug);
                  }
                } else {
                  full_option_slugs[optionType].selected_slugs.splice(index, 1);
                }
              });
            }

            if (
              values[0].option_style === "checkbox_with_dropdown" &&
              values[0].option_type === optionType &&
              full_option_slugs[optionType].selected_slugs.length === 0
            ) {
              full_option_slugs[optionType].sub_options = {};
              jcSelectedOption.current.option_value[optionType] = {
                selected_slugs: [],
                sub_options: {},
              };
            }
          }
        } else {
          full_option_slugs[optionType] = {
            selected_slugs: Array.isArray(selectedValue.selected_slugs)
              ? selectedValue.selected_slugs
              : [],
            sub_options: selectedValue.sub_options
              ? { ...selectedValue.sub_options }
              : {},
          };
        }

        if (Object.entries(selectedValue.sub_options).length === 0) {
          full_option_slugs[optionType].sub_options = {};
        } else if (Object.keys(selectedValue.sub_options).length > 0) {
          full_option_slugs[optionType].sub_options = {
            ...selectedValue.sub_options,
          };
        }
      } else if (Array.isArray(selectedValue)) {
        if (full_option_slugs[optionType]) {
          selectedValue.forEach((value) => {
            const normalizedValue = normalizeSlug(value);
            const normalizedSelectedSlugs = full_option_slugs[optionType].map(
              (s) => normalizeSlug(s)
            );

            const index = normalizedSelectedSlugs.indexOf(normalizedValue);
            if (values[2]) {
              full_option_slugs[optionType] = [];
            }
            if (index === -1) {
              full_option_slugs[optionType]?.push(value); // Add the original value
            } else {
              full_option_slugs[optionType]?.splice(index, 1); // Remove the original value
            }
          });
        } else {
          full_option_slugs[optionType] = selectedValue;
        }
      } else {
        full_option_slugs[optionType] = selectedValue;
      }
      Object.keys(full_option_slugs).forEach((key) => {
        const val = full_option_slugs[key];
        if (
          typeof val === "object" &&
          !Array.isArray(val) &&
          Object.keys(val).length === 0
        ) {
          // If it's an empty object, convert based on original type
          const original = jcSelectedOption.current.option_slug[key];
          if (Array.isArray(original)) {
            full_option_slugs[key] = [];
          } else if (typeof original === "string") {
            full_option_slugs[key] = "";
          } else {
            full_option_slugs[key] = {};
          }
        }
      });

      Object.keys(partial_option_slugs).forEach((key) => {
        const val = partial_option_slugs[key];
        if (
          typeof val === "object" &&
          !Array.isArray(val) &&
          Object.keys(val).length === 0
        ) {
          // If it's an empty object, convert based on original type
          const original = jcSelectedOption.current.option_slug[key];
          if (Array.isArray(original)) {
            partial_option_slugs[key] = [];
          } else if (typeof original === "string") {
            partial_option_slugs[key] = "";
          } else {
            partial_option_slugs[key] = {};
          }
        }
      });
      if (isVariationRequired) {
        getSingleVariation(
          optionType,
          values,
          partial_option_slugs,
          full_option_slugs
        );
      }
    }
  };

  const getSingleVariation = async (
    optionType: string,
    values: [DisplayConfigurationV2, string],
    partial_option_slugs: any,
    full_option_slugs: any
  ) => {
    const filteredPartialSlugs = Object.entries(partial_option_slugs)
      .filter(([_, value]) => value !== "")
      .reduce((acc, [key, value]) => {
        acc[key] = value as string;
        return acc;
      }, {} as Record<string, string>);

    const filteredFullSlugs = Object.entries(full_option_slugs)
      .filter(([_, value]) => value !== "")
      .reduce((acc, [key, value]) => {
        acc[key] = value as string;
        return acc;
      }, {} as Record<string, string>);
    let partial_slug_detail = {};
    let slug_detail = {};
    Object.keys(filteredPartialSlugs).forEach((slug) => {
      const slugValue = filteredPartialSlugs[slug];
      const allItemType = allTypes.find((item) => item.option_type === slug);
      if (typeof slugValue === "string" && allItemType?.option_value) {
        const matchedValue = allItemType.option_value.find(
          (item) => item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
        )?.value;
        if (matchedValue) {
          partial_slug_detail[slug] = matchedValue;
        }
      }
    });
    Object.keys(filteredFullSlugs).forEach((slug) => {
      let allItemType = allTypes.find(function (item) {
        if (item.option_type === slug) {
          if (isRectangularTextButtonWithPrice(item.option_style)) {
            if (
              item.option_type === optionType &&
              !values[0].option_value.find((x) => x.slug === values[1]).enabled
            ) {
              return false;
            } else {
              return false;
            }
          } else {
            return true;
          }
        } else {
          return false;
        }
      });
      if (allItemType?.option_value) {
        let slugValue = filteredFullSlugs[slug];
        if (isObjectWithSelectedSlugs(slugValue)) {
          slug_detail[slug] = slugValue;
        } else if (typeof slugValue === "string") {
          if (
            allItemType.option_value.find(
              (item) =>
                item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
            )?.value
          ) {
            slug_detail[slug] = allItemType.option_value.find(
              (item) =>
                item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
            )?.value;
          }
        } else if (Array.isArray(slugValue)) {
          slug_detail[slug] = slugValue;
        }
      }
    });

    await setDynamicParams({
      ...buildNestedParams("partial_slug_details", partial_slug_detail),
      ...buildNestedParams("slug_details", slug_detail),
      _timestamp: Date.now().toString(),
    });
  };
  const appendNewVariation = (variations: Variations[]) => {
    const idMap = new Map(
      variationListRef.current.map((item) => [item.data.id, item])
    );

    variations.forEach((newItem) => {
      idMap.set(newItem.data.id, newItem); // adds or replaces
    });

    variationListRef.current = Array.from(idMap.values());
  };

  const noPriceForConfiguration = (mItem: any) => {
    if (
      Number(mItem?.total_sales_price) === 0 &&
      !isGuest &&
      getUserPriceMode() !== PriceModes.GREEN
    ) {
      showToast(
        undefined,
        t(
          "productDetailPage.addToCart.noMatch.error.title.text",
          "No price matching the selected configuration."
        ),
        ToastTypes.ERROR,
        true,
        true
      );
    }
  };

  useEffect(() => {
    // Only process variation results (and the "combination not available"
    // toast) for items that actually require variations. Guards against stale
    // VariationData / dynamicParams from a previously opened configurable item
    // triggering the toast on a non-configurable item.
    const requiresVariation =
      isVariationRequired || jewelryItem?.variation_required;
    if (
      requiresVariation &&
      VariationData !== undefined &&
      Object.keys(dynamicParams || {}).length > 0 &&
      isSuccess
    ) {
      const data = VariationData?.data?.response;
      if (data.body.variations.length > 0) {
        const variant = data?.body?.variations.find(
          (item) => item["slug-name"] === variation_slug
        );
        const variation =
          pageSize === 1
            ? data?.body?.variations[0]
            : variant
            ? variant
            : data?.body?.variations[0];
        if (variation) {
          appendNewVariation(data.body.variations);
          setUserSelectedSlug(variation?.item?.slug_detail ?? {});
          updateJcSelectedOptions(variation?.item);
          if (
            Object.keys(dynamicParams || {}).length > 0 &&
            variation?.data?.slug_details &&
            fetchDisabledVariationsForPrices.current
          ) {
            let slug_detail = {};
            let filteredFullSlugs = jcSelectedOption.current?.option_value;
            Object.keys(variation?.data?.slug_details).forEach((slug) => {
              let allItemType = variation?.item?.display_configuration_v2?.find(
                function (item) {
                  if (item.option_type === slug) {
                    if (isRectangularTextButtonWithPrice(item.option_style)) {
                      return false;
                    } else {
                      return true;
                    }
                  } else {
                    return false;
                  }
                }
              );
              if (allItemType?.option_value) {
                let slugValue = filteredFullSlugs[slug];
                if (isObjectWithSelectedSlugs(slugValue)) {
                  slug_detail[slug] = slugValue;
                } else if (typeof slugValue === "string") {
                  if (
                    allItemType.option_value.find(
                      (item) =>
                        item.slug ===
                        slugValue.toLowerCase().replaceAll(" ", "-")
                    )?.value
                  ) {
                    slug_detail[slug] = allItemType.option_value.find(
                      (item) =>
                        item.slug ===
                        slugValue.toLowerCase().replaceAll(" ", "-")
                    )?.value;
                  }
                } else if (Array.isArray(slugValue)) {
                  slug_detail[slug] = slugValue;
                }
              }
            });
            setDynamicParams({
              ...buildNestedParams("slug_details", slug_detail),
            });
            fetchDisabledVariationsForPrices.current = false;
          }
          priceUpdate(variation?.item?.display_configuration_v2);
          if (
            itemDetail?.type === ProductCategory.JEWELRY &&
            Platform.OS === "web"
          ) {
            localStorage.setItem(
              "itemDetail",
              JSON.stringify({
                ...variation?.item,
                variation_required:
                  isVariationRequired ||
                  jewelryItem?.variation_required ||
                  false,
              })
            );
          }
          if (
            itemDetail?.type !== ProductCategory.JEWELRY &&
            Platform.OS === "web"
          ) {
            localStorage.setItem(
              "stoneItemDetails",
              JSON.stringify({
                ...variation?.item,
                variation_required:
                  isVariationRequired ||
                  jewelryItem?.variation_required ||
                  false,
              })
            );
          }
          if (itemDetail?.type === ProductCategory.JEWELRY) {
            setItemDetail({
              ...variation?.item,
              variation_required:
                isVariationRequired || jewelryItem?.variation_required || false,
            });
            noPriceForConfiguration(variation?.item);
          } else {
            setStoneItemDetail({
              ...variation?.item,
              variation_required:
                isVariationRequired || jewelryItem?.variation_required || false,
            });
          }
          const variantItem = variant?.item ?? variation?.item;
          if (
            variantItem &&
            isThreeDEnabled(variantItem) &&
            variantItem?.three_d_media
          ) {
            const threeDMedia = addThreeDAdditionalSettings(
              addThreeDAdditionalSettings(
                variantItem?.three_d_media,
                addThreeDAdditionalSettingsTypes.morph
              ),
              addThreeDAdditionalSettingsTypes.headScale
            );
            DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
              iframeKey: [
                "vdb-gallery-iframe",
                "vdb-sticky-iframe",
                "vdb-overlay-iframe",
              ],
              data: {
                three_d_data_type:
                  initStatus?.three_d_settings?.three_d_data_type || "webgi",
                three_d_media: threeDMedia,
                vjson: initStatus?.three_d_settings?.vjson,
              },
            });
          }
        }
      } else {
        if (itemDetail && itemDetail.type === ProductCategory.JEWELRY) {
          setItemDetail(itemDetail);
          setUserSelectedSlug(itemDetail?.slug_detail ?? {});
          updateJcSelectedOptions(itemDetail);
          setAddToCartPressed(!addToCartPressed);
        } else {
          setStoneItemDetail(itemDetail);
          setUserSelectedSlug(itemDetail?.slug_detail ?? {});
          updateJcSelectedOptions(itemDetail);
          setAddToCartPressed(!addToCartPressed);
        }
        showToast(
          undefined,
          t(
            "productDetailPage.jewelryConfigurator.combinationError.title.text",
            "This combination is not available. Please change your selection"
          ),
          ToastTypes.INFO,
          true,
          true,
          0,
          () => null,
          true,
          4000
        );
      }
    }
  }, [VariationData]);

  useEffect(() => {
    if (allTypes?.length && itemDetail?.custom_design_configuration) {
      if (isThreeDEnabled(itemDetail) && itemDetail?.three_d_media) {
        const threeDMedia = addThreeDAdditionalSettings(
          addThreeDAdditionalSettings(
            itemDetail?.three_d_media,
            addThreeDAdditionalSettingsTypes.morph
          ),
          addThreeDAdditionalSettingsTypes.headScale
        );
        DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
          iframeKey: [
            "vdb-gallery-iframe",
            "vdb-sticky-iframe",
            "vdb-overlay-iframe",
          ],
          data: {
            three_d_data_type:
              initStatus?.three_d_settings?.three_d_data_type || "webgi",
            three_d_media: threeDMedia,
            vjson: initStatus?.three_d_settings?.vjson,
          },
        });
      }
    }
    if (allTypes?.length) {
      applyPersonalizationEngravedText();
    }
  }, [personalization, allTypes]);

  const replaceEmojis = (input: string) => {
    if (input) {
      input = input.replace(/∞/g, "⍔");
      input = input.replace(/♡/g, "⍓");
      input = input.replace(/\u{1F495}/gu, "⍕");
    }
    return input;
  };

  const isEmoji = (character: string) => {
    const emojiRegex =
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{FE00}-\u{FE0F}]|[\u{1F900}-\u{1F9FF}]|[\u{1F1E6}-\u{1F1FF}]|\u{1F004}|\u{1F0CF}|⍓|⍕|⍔|♡/u;
    return emojiRegex.test(character);
  };

  const containsOnlyEmojis = (input: string) => {
    for (const char of input) {
      if (!isEmoji(char)) {
        return false;
      }
    }
    return true;
  };

  const addThreeDAdditionalSettings = (three_d_media: any, type: string) => {
    if (!three_d_media?.length) three_d_media = [];
    three_d_media = three_d_media.filter((x: any) => x.option_type !== type);
    if (
      allTypes &&
      allTypes?.length &&
      [
        addThreeDAdditionalSettingsTypes.all,
        addThreeDAdditionalSettingsTypes.morph,
      ].includes(type)
    ) {
      allTypes.forEach((config) => {
        if (
          config.option_3d_type &&
          config.option_3d_type.includes("morph") &&
          config?.option_3d_type.split(",")?.length === 3
        ) {
          const configArray = config?.option_3d_type
            .split(",")
            .map((x) => x.trim());
          const morphType = configArray[configArray?.length - 1];
          const index = configArray.indexOf("morph");
          if (index > -1) {
            configArray.splice(index, 1);
          }
          let option_values = config.option_value;
          let minValue = option_values?.[0]?.value;
          let maxValue = option_values?.[option_values?.length - 1]?.value;
          let optionValueArray = [
            config?.selected_item,
            minValue,
            maxValue,
            defaultMorphValuesForThinkspace[morphType],
          ];
          let option_identifier = configArray.join();
          three_d_media.push({
            option_type: type,
            option_identifier,
            option_value: optionValueArray.join(","),
          });
        }
      });
    }
    const filteredFields = Object.values(
      personalizationSectionLayout?.["sub-sections"] ?? {}
    ).filter((item) => item.price_modes?.includes(getUserPriceMode()));

    // Check for engrave_font and engrave_text
    const fieldNames = filteredFields.map((item) => item.name);

    const hasEngraveFont = fieldNames.includes("engrave_font");
    const hasEngraveText = fieldNames.includes("engrave_text");

    const hasBothEngravingFields = hasEngraveFont && hasEngraveText;

    if (
      type === addThreeDAdditionalSettingsTypes.engrave &&
      hasBothEngravingFields
    ) {
      const engraveText = replaceEmojis(personalization?.engrave_text ?? "");
      three_d_media.push({
        option_type: addThreeDAdditionalSettingsTypes.engrave,
        option_identifier: "ring",
        option_value: {
          text: engraveText ?? "",
          font:
            engraveText?.length && containsOnlyEmojis(engraveText ?? "")
              ? "emoji"
              : personalization?.engrave_font ?? "",
          size: 0.15,
        },
      });
    }

    if (
      allTypes &&
      allTypes?.length &&
      [addThreeDAdditionalSettingsTypes.headScale].includes(type)
    ) {
      allTypes.forEach((config) => {
        if (config.option_3d_type && config.option_3d_type.includes("scale")) {
          const scaleType = config?.option_3d_type;
          let optionValueArray = [defaultheadScaleValue[scaleType]];
          let option_identifier = "head";
          three_d_media.push({
            option_type: type,
            option_identifier,
            option_value: optionValueArray.join(","),
          });
        }
      });
    }

    const hasScaleOption = allTypes?.some(
      (config) =>
        config?.option_3d_type && config.option_3d_type.includes("scale")
    );
    if (hasScaleOption) {
      const headModel = three_d_media.find(
        (x: any) => x.option_type === "model" && x.option_identifier === "head"
      );
      if (headModel?.option_value) {
        if (!initialHeadModelUrlRef.current) {
          initialHeadModelUrlRef.current = headModel.option_value;
        } else {
          headModel.option_value = initialHeadModelUrlRef.current;
        }
      }
    }

    return three_d_media;
  };

  const applyPersonalizationEngravedText = () => {
    if (isThreeDEnabled(itemDetail) && itemDetail?.three_d_media) {
      if (engraveDebounceRef.current) {
        clearTimeout(engraveDebounceRef.current);
      }
      engraveDebounceRef.current = setTimeout(() => {
        const threeDMedia = addThreeDAdditionalSettings(
          addThreeDAdditionalSettings(
            addThreeDAdditionalSettings(
              itemDetail.three_d_media,
              addThreeDAdditionalSettingsTypes.morph
            ),
            addThreeDAdditionalSettingsTypes.headScale
          ),
          addThreeDAdditionalSettingsTypes.engrave
        );
        DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
          iframeKey: [
            "vdb-gallery-iframe",
            "vdb-sticky-iframe",
            "vdb-overlay-iframe",
          ],
          data: {
            three_d_data_type:
              initStatus?.three_d_settings?.three_d_data_type || "webgi",
            three_d_media: threeDMedia,
            vjson: initStatus?.three_d_settings?.vjson,
          },
        });
      }, 80);
    }
  };

  const applyVariation = (type: string, variation, values) => {
    let allItemType = allTypes.find((item) => item.option_type === type);
    let slugValue = jcSelectedOption.current.option_slug[type];
    if (typeof slugValue === "string") {
      jcSelectedOption.current.option_value[type] =
        allItemType?.option_value?.find(
          (item) => item.slug === slugValue.toLowerCase().replaceAll(" ", "-")
        )?.value;
    } else if (Array.isArray(slugValue)) {
      jcSelectedOption.current.option_value[type] = slugValue;
    } else {
      if (type === "center_measurement") {
        jcSelectedOption.current.option_value[type] = values[1];
      } else {
        const matchedValue = allItemType?.option_value?.find(
          (item) =>
            item.slug === values[1].toLowerCase() || item.value === values[1]
        )?.value;

        jcSelectedOption.current.option_value = {
          ...jcSelectedOption.current.option_value,
          [type]: matchedValue,
        };
      }
    }

    const typeIndex = allTypes.findIndex(
      (allType) => allType.option_type === type
    );
    if (typeIndex > -1) {
      allTypes[typeIndex].selected_item =
        jcSelectedOption.current.option_value[type];
    }
    setShouldSync((prev) => !prev);
  };

  const getOptionSlugsFromTop = useCallback(
    (type: string) => {
      let optionTypes: string[] = [];
      allTypes.every((myType) => {
        if (myType.option_type === type) {
          optionTypes.push(type);
          return false;
        } else {
          optionTypes.push(myType.option_type);
          return true;
        }
      });
      const filtered = Object.keys(jcSelectedOption.current.option_slug)
        .filter((key) => optionTypes.includes(key))
        .reduce((obj, key) => {
          obj[key] = jcSelectedOption.current.option_slug[key];
          return obj;
        }, {} as Record<string, any>);

      return filtered;
    },
    [allTypes, jcSelectedOption]
  );

  const resetDefaultSlug = useCallback(
    async (isJewelryBuilder?: boolean) => {
      setMatchingBandDataState([]);
      const items = isJewelryBuilder
        ? allTypes
        : allTypes.filter((item) => item.is_resettable);
      if (isJewelryBuilder) {
        setPersonalizationState(null);
        jcSelectedOption.current = {
          option_slug: {},
          option_value: {},
          option_required: {},
          option_searchable: {},
          option_mode: {},
          option_visible: [],
          option_error: {},
        };
      } else {
        // Build the full combined slug map in one go
        const full_option_slugs = Object.assign(
          {},
          jcSelectedOption.current.option_slug
        );
        items.forEach((item) => {
          const def = defaultSlug[item.option_type];
          if (typeof def === "string") {
            full_option_slugs[item.option_type] = normalizeSlug(def);
          } else {
            full_option_slugs[item.option_type] = def as any;
          }
        });

        // Update local state for the full variation slug
        setVariationSlug(
          Object.entries(full_option_slugs)
            .map(([_, value]) => value?.toLowerCase().replaceAll(" ", "-"))
            .join("-")
        );

        // Try to resolve locally from the loaded variation list first (no API call)
        const variant = variationListRef.current.find(
          (v) =>
            v["slug-name"] ===
            Object.entries(full_option_slugs)
              .map(([_, value]) => value?.toLowerCase().replaceAll(" ", "-"))
              .join("-")
        );

        if (variant) {
          setUserSelectedSlug(variant?.item?.slug_detail ?? {});
          if (itemDetail?.type === ProductCategory.JEWELRY) {
            setItemDetail(variant?.item);
          } else {
            setStoneItemDetail(variant?.item);
          }
          if (isThreeDEnabled(variant.item) && variant?.item?.three_d_media) {
            DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
              iframeKey: [
                "vdb-gallery-iframe",
                "vdb-sticky-iframe",
                "vdb-overlay-iframe",
              ],
              data: {
                three_d_data_type:
                  initStatus?.three_d_settings?.three_d_data_type || "webgi",
                three_d_media: variant?.item?.three_d_media,
                vjson: initStatus?.three_d_settings?.vjson,
              },
            });
          }
          updateJcSelectedOptions(variant?.item);
        } else {
          // Fallback to a single API fetch with combined params
          const filteredFullSlugs = Object.entries(full_option_slugs)
            .filter(([_, value]) => value !== "")
            .reduce((acc, [key, value]) => {
              acc[key] = value as string;
              return acc;
            }, {} as Record<string, string>);

          let slug_detail = {};
          Object.keys(filteredFullSlugs).forEach((slug) => {
            const allItemType = allTypes.find(
              (item) => item.option_type === slug
            );
            const slugValue = filteredFullSlugs[slug];
            if (isObjectWithSelectedSlugs(slugValue)) {
              (slug_detail as any)[slug] = slugValue;
            } else if (typeof slugValue === "string") {
              const match = allItemType?.option_value?.find(
                (ov) => ov.slug === slugValue.toLowerCase().replaceAll(" ", "-")
              )?.value;
              if (match) {
                (slug_detail as any)[slug] = match;
              }
            } else if (Array.isArray(slugValue)) {
              (slug_detail as any)[slug] = slugValue;
            }
          });

          await setDynamicParams({
            ...buildNestedParams("partial_slug_details", slug_detail),
            ...buildNestedParams("slug_details", slug_detail),
            _timestamp: Date.now().toString(),
          });
        }
      }
    },
    [jcSelectedOption, allTypes]
  );

  function safeParse(value) {
    if (typeof value !== "string") return value;

    const trimmed = value.trim(); // IMPORTANT

    if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
      return value; // clearly not JSON object/array
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  const updateJcSelectedOptions = (variation, enterSecondFlow = false) => {
    const slugDetail = variation?.slug_detail || {};

    allTypes.forEach((type) => {
      const slugDetailKey =
        type?.option_type === "supported_shapes" &&
        !Object.keys(slugDetail).includes("supported_shapes") &&
        Object.keys(slugDetail).includes("supported_shape")
          ? "supported_shape"
          : type?.option_type;

      if (Object.keys(slugDetail).includes(slugDetailKey)) {
        let slugValue =
          variation?.[type.option_type] ??
          variation?.slug_detail?.[slugDetailKey];

        slugValue = safeParse(slugValue);
        jcSelectedOption.current.option_slug[type.option_type] = slugValue;
        // Find corresponding itemType
        const itemType = allTypes.find(
          (item) => item.option_type === type.option_type
        );

        if (typeof slugValue === "string") {
          // Handle string values
          jcSelectedOption.current.option_value[type.option_type] =
            itemType?.option_value?.find(
              (item) =>
                item.slug === slugValue.toLowerCase().replaceAll(" ", "-") ||
                item.value === slugValue
            )?.value;
        } else if (Array.isArray(slugValue)) {
          // Handle array values
          jcSelectedOption.current.option_value[type.option_type] = slugValue;
        } else if (typeof slugValue === "object" && slugValue.selected_slugs) {
          const existingValue = jcSelectedOption.current.option_value[
            type.option_type
          ] || {
            sub_options: {},
            selected_slugs: [],
          };
          const updatedSubOptions = Object.keys(slugValue || {}).length
            ? {
                ...existingValue.sub_options, // Keep existing sub_options
                ...slugValue.sub_options, // Overwrite or add new sub_options
              }
            : {};
          let updatedSelectedSlugs = [
            ...(existingValue.selected_slugs || []), // Keep existing selected slugs
            ...(slugValue.selected_slugs || []).map((slug) => {
              const displayValue = itemType?.option_value?.find(
                (item) => item.slug === slug
              )?.value;
              return {
                normalized: slug,
                display: displayValue || slug,
              }; // Normalize and store display value
            }),
          ]
            .filter((slugObj) => slugObj && slugObj.normalized) // Ensure slugObj is defined and normalized exists
            .map((slugObj) => slugObj.normalized.trim()) // Normalize the slug (lowercase)
            .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates based on normalized slug
          updatedSelectedSlugs = updatedSelectedSlugs.map((normalizedSlug) => {
            const original = itemType?.option_value?.find(
              (item) => item.slug === normalizedSlug
            );
            return original ? original.value : normalizedSlug; // Map back to original display value
          });

          // Update jcSelectedOptions
          jcSelectedOption.current.option_value[type.option_type] = {
            sub_options: updatedSubOptions,
            selected_slugs: updatedSelectedSlugs, // Ensure no duplicates and consistent case
          };
        }
        if (
          type?.option_3d_type &&
          type.option_3d_type.includes("morph") &&
          type?.option_3d_type.split(",")?.length === 3
        ) {
          const morphSplit = type.option_3d_type
            .split(",")
            .map((_type) => _type.trim());
          const morphTtype = morphSplit[morphSplit?.length - 1];
          const newValue = {
            ...defaultMorphValuesForThinkspace,
            [morphTtype]: slugValue,
          };
          setDefaultMorphValuesForThinkspace(newValue);
        }
        if (type?.option_3d_type && type.option_3d_type.includes("scale")) {
          const scaleType = type?.option_3d_type;
          const newValue = {
            ...defaultheadScaleValue,
            [scaleType]: slugValue,
          };
          setDefaultheadScaleValue(newValue);
        }
      }
      // Push visible options
      if (type.option_visible) {
        if (
          !jcSelectedOption.current.option_visible.includes(type.option_type)
        ) {
          jcSelectedOption.current.option_visible.push(type.option_type);
        }
      }
      jcSelectedOption.current.option_required[type.option_type] =
        type.option_required;
    });

    // In stone-to-setting flow the supported_shapes UI is hidden (user cannot
    // select it), so it must not be treated as required for validation.
    if (enterSecondFlow) {
      jcSelectedOption.current.option_required.supported_shapes = false;
    }

    Object.keys(jcSelectedOption.current?.option_value || {}).forEach((key) => {
      const existsInDisplayConfig = itemDetail?.display_configuration_v2?.some(
        (config) => config.option_type === key
      );

      if (!existsInDisplayConfig && !jcSelectedOption.current.option_required) {
        delete jcSelectedOption.current.option_value[key];
      }
    });
    // if (getFeatureSettings().allow_search_modify_in_builder) {
    //   // Cleanup: remove values not in current variation.slug_detail
    //   Object.keys(jcSelectedOption.current.option_value).forEach((key) => {
    //     if (!Object.keys(slugDetail).includes(key)) {
    //       delete jcSelectedOption.current.option_value[key];
    //     }
    //   });
    // }

    // Cleanup: reset option_required for missing types
    Object.keys(jcSelectedOption.current.option_required).forEach(
      (optionType) => {
        if (!allTypes.some((type) => type.option_type === optionType)) {
          jcSelectedOption.current.option_required[optionType] = false;
        }
      }
    );
    setShouldSync((prev) => !prev);
  };

  function validateRequiredOptions() {
    const { option_required, option_value, option_error } =
      jcSelectedOption.current;

    for (const key in option_required) {
      const isRequired = option_required[key];
      const hasValue = option_value.hasOwnProperty(key);

      if (isRequired && !hasValue) {
        option_error[key] = key;
      } else if (
        isRequired &&
        (option_value[key] === null || option_value[key] === undefined)
      ) {
        option_error[key] = key;
      } else if (hasValue && option_error.hasOwnProperty(key)) {
        delete option_error[key];
      }
    }
    setAddToCartPressed(!addToCartPressed);
  }

  function fetchValidationMessage(optionName: string, isInput: boolean) {
    if (isInput) {
      return t(
        "productDetailPage.jewelryConfigurator.inputError.text",
        "Please enter the value(s) to continue"
      );
    }
    return t("productDetailPage.jewelryConfigurator.error.title.text", {
      variationName: optionName,
    });
  }

  const resetVariationList = () => {
    variationListRef.current = [];
  };

  // Clears any previously selected variation slug filters. Must be called when
  // a brand-new product PDP loads so stale params from a prior (configurable)
  // item don't leak into the next item's variation fetch and produce a bogus
  // "combination not available" toast.
  const resetDynamicParams = useCallback(() => {
    setDynamicParams((prev) => (Object.keys(prev ?? {}).length ? {} : prev));
  }, []);

  const value = useMemo(
    () => ({
      defaultSlug,
      setDefaultSlug,
      isVariationRequired,
      setIsVariationRequired,
      allTypes,
      setAllTypes,
      updateJewelryConfigInfo,
      customDesignConfiguration,
      setCustomDesignConfiguration,
      itemDetail,
      setItemDetail,
      isAPILoading,
      resetDefaultSlug,
      userSelectedSlug,
      setUserSelectedSlug,
      personalization,
      setPersonalization,
      hasPersonalizationError,
      setHasPersonalizationError,
      jcSelectedOption,
      updateJcSelectedOptions,
      setAllTypesState,
      validateRequiredOptions,
      addToCartPressed,
      fetchValidationMessage,
      appendNewVariation,
      stoneItemDetail,
      setStoneItemDetail,
      matchingPairItemDetail,
      setMatchingPairItemDetail,
      setReviewTotalPrice,
      reviewTotalPrice,
      addToOption,
      resetVariationList,
      clearItemDetail,
      getDynamicParams,
      addThreeDAdditionalSettings,
      applyPersonalizationEngravedText,
      matchingBandData,
      resetDynamicParams,
    }),
    [
      defaultSlug,
      allTypes,
      itemDetail,
      updateJewelryConfigInfo,
      isVariationRequired,
      jcSelectedOption,
      setItemDetail,
      userSelectedSlug,
      personalization,
      hasPersonalizationError,
      updateJcSelectedOptions,
      setAllTypesState,
      validateRequiredOptions,
      stoneItemDetail,
      setStoneItemDetail,
      matchingPairItemDetail,
      setMatchingPairItemDetail,
      reviewTotalPrice,
      addToOption,
      resetVariationList,
      clearItemDetail,
      getDynamicParams,
      addThreeDAdditionalSettings,
      applyPersonalizationEngravedText,
      matchingBandData,
      resetDynamicParams,
    ]
  );

  return (
    <JewelryConfiguratorContext.Provider value={value}>
      {children}
    </JewelryConfiguratorContext.Provider>
  );
};

export const useJewelryConfiguratorState = () => {
  const context = useContext(JewelryConfiguratorContext);
  if (!context) {
    throw new Error(
      "JewelryConfiguratorContext must be used within a JewelryConfiguratorProvider"
    );
  }
  return context;
};
